import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { map, Observable } from 'rxjs';
import { ProductPanelHelper } from '../product-panel/product-panel-helper';
import { EsraPipingAPIClient, ShareLinkResponse, ValveViewModel } from '../../models/autogenerated-piping';
import { PipingAndValvesNavigationService } from 'src/app/piping-valves/services/piping-and-valves-navigation.service';
import { PipingAndValvesDocumentService } from 'src/app/piping-valves/services/piping-and-valves-document.service';
import { Router } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';

@Component({
    selector: 'app-valve-panel',
    templateUrl: './valve-panel.component.html',
    styleUrls: ['./valve-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ValvePanelComponent implements OnInit {
  @Input() valve: ValveViewModel;
  @Input() addWorkspaceShow: boolean;
  @Input() isAdminSearch: boolean;

  @Output() openPage = new EventEmitter<string>();
  @Output() valveSelected = new EventEmitter<boolean>();
  @Output() valveId = new EventEmitter<string>();
  @Output() archiveValve = new EventEmitter<string>();
  isPipingAndValvesAdmin$: Observable<boolean>;

  constructor(
    private productPanelHelper: ProductPanelHelper,
    private pipingAndValvesNavigationService: PipingAndValvesNavigationService,
    private pipingAndValvesDocumentService: PipingAndValvesDocumentService,
    private router: Router,
    private userService: UserService,
    private readonly esraPipingAPIClient: EsraPipingAPIClient
  ) {}

  /**
   * Builds the fetch function passed to `app-share-link-button` for this card's valve.
   * @param valveId The valve id to build the share link for.
   * @returns A function returning an Observable of the absolute share URL.
   */
  getValveShareUrlFn = (valveId: string): () => Observable<string> =>
    () => this.esraPipingAPIClient.valveShareLink(valveId).pipe(
      map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
    );

  getValvePdfShareUrlFn = (recordId: string): () => Observable<string> =>
    () => this.esraPipingAPIClient.valvePdfShareLink(recordId).pipe(
      map((response: ShareLinkResponse) => `${window.location.origin}/${(response.relativeUrl ?? '').replace(/^\/+/, '')}`)
    );

  ngOnInit() {
    this.addWorkspaceShow = true;
    this.isPipingAndValvesAdmin$ = this.userService.isPipingAndValvesAdminObservable();

    this.valve.tag = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.tag
    );
    this.valve.businessUnits = this.productPanelHelper.arrReplaceHighlightHtml(
      this.valve.businessUnits
    );

    this.valve.application = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.application
    );
    this.valve.componentTypeString =
      this.productPanelHelper.strReplaceHighlightHtml(
        this.valve.componentTypeString
      );

    this.valve.rating = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.rating
    );
    this.valve.ends = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.ends
    );
    this.valve.body = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.body
    );

    this.valve.trim = this.productPanelHelper.strReplaceHighlightHtml(
      this.valve.trim
    );
    this.valve.pipeClassNames = this.highlightStringList(
      this.valve.pipeClassNames
    );

    if (this.valve.fromRevision) {
      this.valve.fromRevision.version =
        this.productPanelHelper.strReplaceHighlightHtml(
          this.valve.fromRevision.version
        );
    }
  }

  highlightStringList(stringList: string[]): string[] {
    const returnStringArray: Array<string> = [];
    for (const string in stringList) {
      returnStringArray.push(
        this.productPanelHelper.strReplaceHighlightHtml(string)
      );
    }
    return returnStringArray;
  }

  public stripEmphasisClassFromString(title: string): string {
    const openTagString = '<em>';
    const closeTagString = '</em>';
    if (title) {
      title = title.replace(new RegExp(openTagString, 'g'), '');
      title = title.replace(new RegExp(closeTagString, 'g'), '');
    }
    return title;
  }

  onArchiveToggle(): void {
    if (!this.valve?.id) {
      return;
    }

    if (!this.valve.isArchived) {
      this.archiveValve.emit(this.valve.id);
    }
  }

  stripTrailingCharactersFromValveTag(tag: string): string {
    if (tag) {
      const delimiter = '_';
      if (tag.includes(delimiter)) {
        const substring = tag.split(delimiter);
        return substring[0];
      }
    }
    return tag;
  }

  getValueOrDash(value) {
    if (value === undefined || value === null) {
      value = '-';
    }
    return value;
  }

  emitValveResultSelected(): void {
    if (this.isAdminSearch) {
      this.valveId.emit(this.valve.id);
      this.valveSelected.emit(true);
    } else {
      this.pipingAndValvesDocumentService.openPDF(this.valve.id, 'pv');
    }
  }

  navigateToValveTagSummary(): void {
    if (this.isAdminSearch) {
      this.router.navigate(['/valvesummary', this.valve.id]);
    } else {
      this.pipingAndValvesDocumentService.openPDF(this.valve.id, 'pv');
    }
  }
}
